import { Controller } from "@hotwired/stimulus";

// The one-page registration of a new student (owner's request of 14/10/2026):
// the student, their parents, their enrolment. The server answers every
// question with HTML — the students of the school a search names, the parent
// a phone names, the probable duplicates — and asks them all again on submit.
export default class extends Controller {
  static targets = ["searchInput", "searchResults", "parents", "template", "duplicates", "enrolment", "classroom"];
  static values = { searchUrl: String, lookupUrl: String, duplicatesUrl: String };

  connect() {
    this.renumberParents();
  }

  disconnect() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.duplicatesTimer);
  }

  // The students of the school: found, the pupil is enrolled, not created.
  search() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(async () => {
      const term = this.searchInputTarget.value.trim();
      if (term.length < 2) {
        this.searchResultsTarget.innerHTML = "";
        return;
      }
      const html = await this.fetchHtml(this.searchUrlValue, { search_term: term });
      if (this.searchInputTarget.value.trim() === term) this.searchResultsTarget.innerHTML = html;
    }, 300);
  }

  addParent() {
    const html = this.templateTarget.innerHTML.replaceAll("__INDEX__", `${Date.now()}`);
    this.parentsTarget.insertAdjacentHTML("beforeend", html);
    this.renumberParents();
  }

  // One parent at least stays on the page.
  removeParent(event) {
    const rows = this.parentRows();
    if (rows.length <= 1) return;

    event.currentTarget.closest("[data-parent-row]").remove();
    this.renumberParents();
    this.checkDuplicates();
  }

  // The parents a few typed letters name, as the student search does.
  lookUp(event) {
    const row = event.currentTarget.closest("[data-parent-row]");
    clearTimeout(row.lookupTimer);
    row.lookupTimer = setTimeout(async () => {
      const term = row.querySelector("[data-lookup-input]").value.trim();
      const result = row.querySelector("[data-lookup-result]");
      if (term.length < 2) {
        result.innerHTML = "";
        return;
      }
      const html = await this.fetchHtml(this.lookupUrlValue, { term });
      // A slower answer to an older term never replaces a newer one.
      if (row.querySelector("[data-lookup-input]").value.trim() === term) result.innerHTML = html;
    }, 300);
  }

  linkParent(event) {
    const row = event.currentTarget.closest("[data-parent-row]");
    const found = event.currentTarget.closest("[data-found-parent]");
    const template = found.querySelector("template[data-linked-template]");
    row.querySelector("[data-parent-id]").value = event.currentTarget.dataset.parentId;
    row.querySelector("[data-lookup-result]").innerHTML = template.innerHTML;
    this.setLinked(row, true);
    this.checkDuplicates();
  }

  unlinkParent(event) {
    const row = event.currentTarget.closest("[data-parent-row]");
    row.querySelector("[data-parent-id]").value = "";
    row.querySelector("[data-lookup-result]").innerHTML = "";
    this.setLinked(row, false);
    this.checkDuplicates();
  }

  // Lives with the student: a copy of their address; otherwise their own.
  toggleLives(event) {
    const row = event.currentTarget.closest("[data-parent-row]");
    row.querySelector("[data-own-address]").hidden = event.currentTarget.checked;
  }

  toggleEnrolment(event) {
    const enrol = event.currentTarget.checked;
    this.enrolmentTarget.hidden = !enrol;
    if (this.hasClassroomTarget) this.classroomTarget.required = enrol;
  }

  // The pupil who comes back « as a new student »: shown while typing.
  checkDuplicates() {
    clearTimeout(this.duplicatesTimer);
    this.duplicatesTimer = setTimeout(async () => {
      const field = (name) => this.element.querySelector(`[name="student[${name}]"]`)?.value?.trim() || "";
      const params = new URLSearchParams({
        first_name: field("first_name"),
        last_name: field("last_name"),
        birth_date: field("birth_date"),
        place_of_birth: field("place_of_birth"),
      });
      this.element.querySelectorAll('[name$="[phone]"]').forEach((input) => {
        if (!input.disabled && input.value.trim() !== "") params.append("phones[]", input.value.trim());
      });
      if (params.get("first_name") === "" || params.get("last_name") === "") return;

      const html = await this.fetchHtml(this.duplicatesUrlValue, params);
      // The user's answer is kept while the same students come back.
      const confirmed = this.duplicatesTarget.querySelector('[name="duplicate_confirmed"]')?.checked;
      this.duplicatesTarget.innerHTML = html;
      const checkbox = this.duplicatesTarget.querySelector('[name="duplicate_confirmed"]');
      if (checkbox && confirmed) checkbox.checked = true;
    }, 400);
  }

  // private

  setLinked(row, linked) {
    row.querySelector("[data-lookup]").hidden = linked;
    row.querySelector("[data-new-parent]").hidden = linked;
    row.querySelector("[data-known-address-note]").hidden = !linked;
    row.querySelectorAll("[data-new-parent] input").forEach((input) => {
      input.disabled = linked;
      if (input.type !== "email") input.required = !linked;
    });
  }

  parentRows() {
    return Array.from(this.parentsTarget.querySelectorAll("[data-parent-row]"));
  }

  renumberParents() {
    if (!this.hasParentsTarget) return;

    const rows = this.parentRows();
    rows.forEach((row, position) => {
      const title = row.querySelector("[data-parent-row-title]");
      if (title) title.textContent = title.textContent.replace(/\s*\d*$/, ` ${position + 1}`);
      const remove = row.querySelector('[data-action~="student-registration#removeParent"]');
      if (remove) remove.hidden = rows.length <= 1;
    });
  }

  async fetchHtml(url, params) {
    const query = params instanceof URLSearchParams ? params : new URLSearchParams(params);
    try {
      const response = await fetch(`${url}?${query.toString()}`, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      });
      return response.ok ? await response.text() : "";
    } catch {
      return "";
    }
  }
}
